<template lang="pug">
.overview-tab
  .overview-block.contact-info
    .location-info(v-if="formattedAddress")
      Icon.location-icon(name="location" fill="black")
      .address {{ formattedAddress }}
    .phone-info(v-if="gym.phone")
      Icon.phone-icon(name="telephone" fill="black")
      a.phone-number(:href="`tel:${gym.phone}`") {{ gym.phone }}
    .training-info(v-if="gym.trainingTypes?.length")
      Icon.training-icon(name="training" fill="black")
      .training-types {{ gym.trainingTypes.join(', ') }}
    .website-info(v-if="websiteLinks.length" v-for="websiteLink in websiteLinks")
      Icon.link-icon(name="link" fill="black")
      a.website-link(:href="websiteLink.url" target="_blank") {{ websiteLink.url }}
    .other-info
      Icon.share-icon(name="share" fill="black")
      RogueButton.share-link(color="link-black" @click="shareGym = true") {{ __('Share Gym') }}
  .overview-block.social(v-if="socialLinks.length")
    h5.block-header {{ __('Social Links') }}
    .social-links
      a.social-link(v-for="link in socialLinks" :href="link.url" target="_blank")
        Icon(v-if="link.type === 'facebook'" name="facebook-black")
        Icon(v-else-if="link.type === 'instagram'" name="instagram-black")
        Icon(v-else-if="link.type === 'tiktok'" name="tiktok-black")
        Icon(v-else-if="link.type === 'x' || link.type === 'twitter'" name="twitter-x-black")
        Icon(v-else-if="link.type === 'youtube'" name="youtube-black")
  .overview-block.description(v-if="!!gym.description")
    h5.block-header {{ __('Gym Description') }}
    p.description {{ gym.description }}
  .overview-block.equipment(v-if="products.length")
    h5.block-header {{ __('Rogue Equipment') }}
    Carousel(theme="no-pad")
      .featured-equipment
        .product-link(v-for="product in products" @click="$emit('featureProduct', product)")
          .product-thumb
            StaticImage(:src="product.thumbnail" :width="83" :alt="product.name")
          .product-name {{ product.name }}
  .overview-block.gallery(v-if="gym.media?.length")
    h5.block-header {{ __('Images & Videos') }}
    Carousel(theme="no-pad")
      .gym-media
        StaticImage.media-thumb(
          v-for="(image, i) in gym.media"
          :src="image.src"
          :alt="__('Gym image {n}', { n: String(i + 1) })"
          :width="83"
          @click="selectedImage = i; showImageGallery = true"
        )
  Modal.share-gym-modal(v-if="shareGym" @close="shareGym = false" width="530px")
    .share-modal-body
      h2.share-gym-header {{ __('Share the Gym') }}
      p.share-instructions {{ __('Share the gym\'s profile by copying the link or posting to Facebook or X.') }}
      .share-buttons
        RogueButton.copy-button(@click="copyLink()")
          .share-button-content
            Icon.copy-btn-icon(:name="copySuccess ? 'check' : 'copy'" fill="black")
            | {{ __(copyButtonText) }}
        RogueButton.share-button(v-for="service in SERVICES" :key="service.key" @click="share(service)")
          .share-button-content
            Icon.share-btn-icon(:name="service.icon" fill="black")
            | {{ __(service.text) }}
  CarouselModal(v-if="showImageGallery" @close="showImageGallery = false" :value="selectedImage")
    DynamicMedia.zoom-horiz-img(
      v-for="image in gym.media"
      :key="image.src"
      :config="{ src: image.src, autoHeight: true, enableVideoControls: true }"
      :contain="true"
    )
</template>

<script setup lang="ts">
import type { IGymLink, IGym, IGymProduct } from '@rogue/universal'
import { computed, ref } from 'vue'
import { formatGymAddress } from './gymHelpers'
import { useRogue } from '@/js/services/Rogue'

const rogue = useRogue()

const props = defineProps<{
  gym: IGym
}>()

defineEmits<{(e: 'featureProduct', product: IGymProduct): void }>()

const shareGym = ref(false)
const copySuccess = ref(false)
const showImageGallery = ref(false)
const selectedImage = ref(0)

const products = computed<IGymProduct[]>(() => props.gym.products || [])
const copyButtonText = computed(() => (copySuccess.value ? 'Link Copied to Clipboard' : 'Copy Link'))
const socialLinks = computed<IGymLink[]>(() => props.gym.social?.filter(link => link.type !== 'website') || [])
const websiteLinks = computed<IGymLink[]>(() => props.gym.social?.filter(link => link.type === 'website') || [])

const formattedAddress = formatGymAddress(props.gym.address, !props.gym.shareAddress)

const SERVICES = ref([
  {
    icon         : 'facebook',
    key          : 'facebook',
    text         : 'Facebook',
    baseLink     : 'https://www.facebook.com/sharer/sharer.php?',
    urlParams    : (options: any) => ({ u: options.url }),
    windowParams : 'width=580,height=296',
  },
  {
    icon         : 'twitter',
    key          : 'x',
    text         : 'X',
    baseLink     : 'https://twitter.com/intent/tweet?',
    urlParams    : (options: any) => ({ text: options.text, url: options.url }),
    windowParams : 'width=550,height=270',
  },
])

async function copyLink() {
  try {
    await navigator.clipboard.writeText(window.location.href)
    copySuccess.value = true
    setTimeout(() => {
      copySuccess.value = false
    }, 3000)
  } catch (error: any) {
    rogue.log.warn('Unable to copy path to clipboard', error)
  }
}

// This is patterned off of the share function in social-share.vue
function share(service: any) {
  const data = {
    title : document.title,
    text  : `${props.gym.name} - ${props.gym.description}`,
    url   : window.location,
    image : props.gym.logo,
  }

  const params = Object.entries(service.urlParams(data)).map(([key, val]) => `${key}=${val}`).join('&')
  const url = service.baseLink + params

  window.open(encodeURI(url), '_blank', service.windowParams)
}

</script>

<style lang="scss" scoped>
.overview-block {
  border-bottom: 1px solid $color-gray;
  padding: $space-s;

  &:last-of-type {
    border-bottom: none;
  }
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: $space-s;
}

.phone-info,
.location-info,
.other-info,
.training-info,
.website-info {
  display: flex;
  flex-direction: row;
  gap: $space-xxs;
}

.block-header {
  margin-bottom: $space-xs;
  text-transform: uppercase;
}

.phone-icon,
.location-icon,
.share-icon,
.training-icon,
.link-icon {
  color: $color-black;
  height: $size-xs;
  width: $size-xs;
  margin-top: 2px;
  flex-shrink: 0;
}

.overview-tab {

  p.description,
  .phone-number,
  .address,
  .share-link,
  .website-link,
  .training-types {
    color: $color-black;
    font-size: $size-xs;
    line-height: $size-m;
    margin: 0;
    padding: 0;
  }
}

.social {
  .social-links {
    display: flex;
    flex-direction: row;
    gap: $space-s;

    .social-link .icon {
      height: $space-s;
      width: $space-s;
    }
  }
}

.zoom-horiz-img {
  max-width: 100%;
  max-height: 100%;
  height: 100%;

  :deep(.rg-img) {
    background-color: transparent;
  }
}

.share-gym-modal {
  .share-modal-body {
    padding: 0 $space-m;
  }

  .share-gym-header {
    margin-bottom: $space-m;
    text-align: center;
  }

  .share-instructions {
    font-size: $size-xs;
    margin-bottom: $space-m;
    text-align: center;
  }

  .share-buttons {
    display: flex;
    flex-direction: column;
    gap: $space-s;

    .copy-button {
      padding-bottom: $space-xs;
      padding-top: $space-xs;
    }

    .share-button {
      padding-bottom: $space-xxxs;
      padding-top: $space-xxxs;
    }
  }

  .share-button-content {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  .copy-btn-icon {
    margin-right: $space-xxs;
  }

  .share-btn-icon {
    height: 36px;
    width: 36px;
  }
}

.featured-equipment {
  display: flex;
  flex-direction: row;
  gap: $space-s;

  .product-link {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
    width: 83px;

    &:hover {
      cursor: pointer;
    }
  }

  .product-thumb {
    width: 83px;
  }

  .product-name {
    color: $color-black-light;
    font-size: $size-xxs;
    line-height: $size-xs;
    width: 83px;
  }
}

.gym-media {
  display: flex;
  flex-direction: row;
  gap: $space-s;

  .media-thumb {
    width: 83px;

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
